import {
  faChartSimple,
  faCircleExclamation,
  faCode,
  faTable,
} from "@fortawesome/pro-regular-svg-icons";
import { format } from "sql-formatter";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { type TabData, Tabs } from "src/base-components/Tabs";
import {
  type ToolCallContentBlock,
  type ToolMessage,
} from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { useMetricsQuery } from "src/flow/analytics/m1/useMetricsQuery";
import { MetricsChart } from "src/flow/analytics/m1/VisualizeMetrics/MetricsChart";
import { MetricsDataTable } from "src/flow/analytics/m1/VisualizeMetrics/MetricsDataTable";

type VisualizeMetricsArgs = {
  sql?: string;
  chartDefinition?: Record<string, unknown>;
};

const getArgs = (toolCall: ToolCallContentBlock): VisualizeMetricsArgs => {
  const args = toolCall.args as Record<string, unknown>;
  const chart = args.chart_definition;
  return {
    sql: typeof args.sql === "string" ? args.sql : undefined,
    chartDefinition:
      chart && typeof chart === "object" && !Array.isArray(chart)
        ? (chart as Record<string, unknown>)
        : undefined,
  };
};

const ChartPanel: React.FC<{
  sql: string | undefined;
  chartDefinition: Record<string, unknown> | undefined;
}> = ({ sql, chartDefinition }) => {
  const { data: rows, isLoading, error } = useMetricsQuery(sql);

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (!chartDefinition) {
    return (
      <EmptyState
        description="Provide a chart definition to render a visualization."
        headline="No chart definition"
        icon={faChartSimple}
        size="compact"
      />
    );
  }
  if (isLoading) {
    return <SkeletonPlaceholder height="h-full" width="w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faChartSimple}
        size="compact"
      />
    );
  }

  return <MetricsChart chartDefinition={chartDefinition} rows={rows} />;
};

const formatSql = (sql: string): string => {
  try {
    return format(sql, { language: "postgresql" });
  } catch {
    return sql;
  }
};

const SqlPanel: React.FC<{ sql: string | undefined }> = ({ sql }) => {
  if (!sql) {
    return <div className="font-inter-normal-12px text-gray-400">No SQL</div>;
  }
  return (
    <CodeEditor language="sql" lineWrapping readOnly value={formatSql(sql)} />
  );
};

const TablePanel: React.FC<{ sql: string | undefined }> = ({ sql }) => {
  const { data: rows, isLoading, error } = useMetricsQuery(sql);

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!isLoading && (!rows || rows.length === 0)) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faTable}
        size="compact"
      />
    );
  }

  return <MetricsDataTable isLoading={isLoading} rows={rows ?? []} />;
};

export const VisualizeMetricsToolMessage: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
}> = ({ toolCall }) => {
  const { sql, chartDefinition } = getArgs(toolCall);

  const tabs: TabData[] = [
    {
      key: "chart",
      label: "Chart",
      content: <ChartPanel chartDefinition={chartDefinition} sql={sql} />,
    },
    { key: "sql", label: "SQL", content: <SqlPanel sql={sql} /> },
    { key: "table", label: "Table", content: <TablePanel sql={sql} /> },
  ];

  return (
    <div className="w-full" data-loc="visualize-metrics-tool-message">
      <Tabs
        containerClassName="w-full"
        defaultActiveKey="chart"
        panelClassName="h-80 w-full pt-3"
        tabClassName="px-2"
        tabListClassName="border-b border-gray-200"
        tabs={tabs}
      />
    </div>
  );
};
